/**
 * Add Organization Address Form Schema
 *
 * Defines validation for creating/updating organization addresses.
 * addressId and timestamps are backend-managed.
 *
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Address Type Enum (exactly 5 options)
// ============================================================================

export const AddressTypeEnum = z.enum([
  'Shipping Address',
  'Billing Address',
  'Head Office',
  'Branch Office',
  'Warehouse',
])
export type AddressType = z.infer<typeof AddressTypeEnum>

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddOrganizationAddressFormSchema = z.object({
  addressName: z
    .string()
    .min(1, 'Address name is required'),

  addressType: AddressTypeEnum,

  phone: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Phone number is invalid (enter 7-15 digits including the country code)')
    .optional()
    .or(z.literal('')),

  street: z
    .string()
    .min(1, 'Street is required'),

  city: z
    .string()
    .min(1, 'City is required'),

  state: z
    .string()
    .min(1, 'State/Province is required'),

  postalCode: z
    .string()
    .min(1, 'Postal code is required'),

  country: z
    .string()
    .min(1, 'Country is required'),

  // Optional for edit mode; included when updating existing address
  id: z.string().optional(),

  // Address number for edit operations
  addressNumber: z.number().optional(),
})

export type AddOrganizationAddressFormData = z.infer<typeof AddOrganizationAddressFormSchema>

// ============================================================================
// Field Validation (shared by add and edit composers)
// ============================================================================

export const validateField = (
  name: keyof AddOrganizationAddressFormData,
  value: unknown,
): string | null => {
  const shape = AddOrganizationAddressFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// API Request Transformation
// ============================================================================

/**
 * Transform form data to the backend `upsertOrganizationAddress` contract.
 *
 * Field-name alignment (frontend → backend):
 *   organizationId (string business number) → orgNumber (integer)
 *   street                                  → addressLine1
 * `addressNumber` is the business key for updates. `id`/`phone` are dropped —
 * the backend contract (and mint.addresses table) have no such fields.
 *
 * @param organizationId - The owning org's numeric business key (orgNumber), as a string.
 */
export function transformToApiRequest(
  formData: AddOrganizationAddressFormData,
  organizationId: string
) {
  return {
    addressNumber: formData.addressNumber,
    orgNumber: Number(organizationId),
    addressName: formData.addressName,
    addressType: formData.addressType,
    addressLine1: formData.street,
    city: formData.city,
    state: formData.state,
    postalCode: formData.postalCode,
    country: formData.country,
  }
}
